// src/pages/admin/ConfigurationsPage.jsx
import React, { useState } from 'react';

const ConfigurationsPage = () => {
  // State for different configuration sections
  const [activeTab, setActiveTab] = useState('system');
  
  // System configuration state
  const [systemConfig, setSystemConfig] = useState({
    appName: 'EBIOS RM',
    appLogo: '',
    defaultLanguage: 'fr',
    emailHost: '',
    emailPort: '',
    emailUser: '',
    emailPassword: '',
    notificationsEnabled: true
  });
  
  // User management configuration state
  const [userConfig, setUserConfig] = useState({
    allowRegistration: true,
    defaultUserRole: 'simpleuser',
    passwordMinLength: 8,
    requireEmailVerification: true
  });
  
  // Subscription configuration state
  const [subscriptionConfig, setSubscriptionConfig] = useState({
    defaultPlan: 'basic',
    trialPeriodDays: 14,
    autoRenewal: true,
    currency: 'EUR'
  });
  
  // Audit configuration state
  const [auditConfig, setAuditConfig] = useState({
    logRetentionDays: 90,
    exportFormat: 'csv',
    gdprCompliance: true
  });

  // Handle system config changes
  const handleSystemConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSystemConfig({
      ...systemConfig,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle user config changes
  const handleUserConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserConfig({
      ...userConfig,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle subscription config changes
  const handleSubscriptionConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubscriptionConfig({
      ...subscriptionConfig,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Handle audit config changes
  const handleAuditConfigChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAuditConfig({
      ...auditConfig,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Save configurations
  const saveConfigurations = () => {
    // In a real app, this would make API calls to save the configurations
    console.log('Saving configurations:', {
      systemConfig,
      userConfig,
      subscriptionConfig,
      auditConfig
    });
    alert('Configurations saved successfully!');
  };

  // Reset to default configurations
  const resetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all configurations to default values?')) {
      setSystemConfig({
        appName: 'EBIOS RM',
        appLogo: '',
        defaultLanguage: 'fr',
        emailHost: '',
        emailPort: '',
        emailUser: '',
        emailPassword: '',
        notificationsEnabled: true
      });
      
      setUserConfig({
        allowRegistration: true,
        defaultUserRole: 'simpleuser',
        passwordMinLength: 8,
        requireEmailVerification: true
      });
      
      setSubscriptionConfig({
        defaultPlan: 'basic',
        trialPeriodDays: 14,
        autoRenewal: true,
        currency: 'EUR'
      });
      
      setAuditConfig({
        logRetentionDays: 90,
        exportFormat: 'csv',
        gdprCompliance: true
      });
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Configurations du Système</h1>
        <div className="flex space-x-3">
          <button
            onClick={resetToDefaults}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Réinitialiser
          </button>
          <button
            onClick={saveConfigurations}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Enregistrer
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('system')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'system'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Configuration Système
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Gestion des Utilisateurs
          </button>
          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'subscriptions'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Abonnements
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'audit'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Audit & Conformité
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        {/* System Configuration Tab */}
        {activeTab === 'system' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Configuration Système</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom de l'application
                </label>
                <input
                  type="text"
                  name="appName"
                  value={systemConfig.appName}
                  onChange={handleSystemConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Langue par défaut
                </label>
                <select
                  name="defaultLanguage"
                  value={systemConfig.defaultLanguage}
                  onChange={handleSystemConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="fr">Français</option>
                  <option value="en">Anglais</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hôte SMTP
                </label>
                <input
                  type="text"
                  name="emailHost"
                  value={systemConfig.emailHost}
                  onChange={handleSystemConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Port SMTP
                </label>
                <input
                  type="number"
                  name="emailPort"
                  value={systemConfig.emailPort}
                  onChange={handleSystemConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Utilisateur SMTP
                </label>
                <input
                  type="text"
                  name="emailUser"
                  value={systemConfig.emailUser}
                  onChange={handleSystemConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe SMTP
                </label>
                <input
                  type="password"
                  name="emailPassword"
                  value={systemConfig.emailPassword}
                  onChange={handleSystemConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="notificationsEnabled"
                  checked={systemConfig.notificationsEnabled}
                  onChange={handleSystemConfigChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Notifications activées
                </label>
              </div>
            </div>
          </div>
        )}

        {/* User Management Configuration Tab */}
        {activeTab === 'users' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Gestion des Utilisateurs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="allowRegistration"
                  checked={userConfig.allowRegistration}
                  onChange={handleUserConfigChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Autoriser l'inscription
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle par défaut
                </label>
                <select
                  name="defaultUserRole"
                  value={userConfig.defaultUserRole}
                  onChange={handleUserConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="simpleuser">Utilisateur simple</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Longueur minimale du mot de passe
                </label>
                <input
                  type="number"
                  name="passwordMinLength"
                  value={userConfig.passwordMinLength}
                  onChange={handleUserConfigChange}
                  min="6"
                  max="128"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="requireEmailVerification"
                  checked={userConfig.requireEmailVerification}
                  onChange={handleUserConfigChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Vérification d'e-mail requise
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Configuration Tab */}
        {activeTab === 'subscriptions' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Configuration des Abonnements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan par défaut
                </label>
                <select
                  name="defaultPlan"
                  value={subscriptionConfig.defaultPlan}
                  onChange={handleSubscriptionConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="basic">Basique</option>
                  <option value="premium">Premium</option>
                  <option value="enterprise">Entreprise</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Période d'essai (jours)
                </label>
                <input
                  type="number"
                  name="trialPeriodDays"
                  value={subscriptionConfig.trialPeriodDays}
                  onChange={handleSubscriptionConfigChange}
                  min="0"
                  max="365"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="autoRenewal"
                  checked={subscriptionConfig.autoRenewal}
                  onChange={handleSubscriptionConfigChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Renouvellement automatique
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Devise
                </label>
                <select
                  name="currency"
                  value={subscriptionConfig.currency}
                  onChange={handleSubscriptionConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="EUR">Euro (€)</option>
                  <option value="USD">Dollar ($)</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Audit & Compliance Configuration Tab */}
        {activeTab === 'audit' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Audit & Conformité</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conservation des journaux (jours)
                </label>
                <input
                  type="number"
                  name="logRetentionDays"
                  value={auditConfig.logRetentionDays}
                  onChange={handleAuditConfigChange}
                  min="1"
                  max="3650"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Format d'exportation
                </label>
                <select
                  name="exportFormat"
                  value={auditConfig.exportFormat}
                  onChange={handleAuditConfigChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                  <option value="xml">XML</option>
                </select>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="gdprCompliance"
                  checked={auditConfig.gdprCompliance}
                  onChange={handleAuditConfigChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Conformité RGPD
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfigurationsPage;